import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { savePermitForm, updatePermitForm } from '../../store/slices/projectPermitsSlice';
import { showToast } from '../utils/Toast';
import { fetchProjectData, prefillFormData } from '../../utils/projectDataUtils';
import { printPermitForm, createCompanyHeader, formatFormField, formatTableRow } from '../../utils/PermitPrintUtils';
import FloatingActionBar from './FloatingActionBar';
import { config } from '../../config/config';

const BidNotesForm = ({ projectId, currentPermit, onClose }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    jobNumber: '',
    date: '',
    squareFootage: '',
    roofPitch: '',
    eaveLength: '',
    rakeLength: '',
    ridgeLength: '',
    valleyLength: '',
    eveRakeLength: '',
    hwfLength: '',
    swfLength: '',
    dtwLength: '',
    dripEdgeLength: '',
    copingLength: '',
    wallsLength: '',
    drains: '',
    scuppers: '',
    overflows: '',
    guttersLength: '',
    downSpoutsLength: '',
    leaderHeadsQty: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentPermit && currentPermit.formType === 'bidNotes') {
      setFormData(currentPermit.formData || formData);
    } else {
      // Fetch project data to prefill the form
      const getProjectData = async () => {
        try {
          // Try using the utility function first
          const projectData = await fetchProjectData(projectId);
          console.log('Project data from utility:', projectData);
          
          // Handle the case where the project is nested in a 'project' property
          const actualProject = projectData.project || projectData;
          console.log('Actual project data to use:', actualProject);
          
          // Explicitly extract job number from the project data
          let jobNumber = '';
          
          // Check for job number in various fields
          if (actualProject.jobNumber) {
            jobNumber = actualProject.jobNumber;
            console.log('Found jobNumber in project:', jobNumber);
          } else if (actualProject.projectNumber) {
            jobNumber = actualProject.projectNumber;
            console.log('Found projectNumber in project:', jobNumber);
          } else if (actualProject.number) {
            jobNumber = actualProject.number;
            console.log('Found number in project:', jobNumber);
          } else if (actualProject._id) {
            jobNumber = actualProject._id;
            console.log('Using _id as fallback:', jobNumber);
          }
          
          // Check originalLead for job number if not found yet
          if (!jobNumber && actualProject.originalLead && actualProject.originalLead.jobNumber) {
            jobNumber = actualProject.originalLead.jobNumber;
            console.log('Found jobNumber in originalLead:', jobNumber);
          }
          
          // Prefill the form data
          const prefilled = prefillFormData(actualProject, formData);
          console.log('Prefilled form data:', prefilled);
          
          // Explicitly set job number
          if (jobNumber) {
            prefilled.jobNumber = jobNumber;
            console.log('Setting job number to:', jobNumber);
          }
          
          // Ensure date is set to today
          const now = new Date();
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          const day = String(now.getDate()).padStart(2, '0');
          prefilled.date = `${year}-${month}-${day}`;
          console.log('Setting date to today:', prefilled.date);
          
          setFormData(prefilled);
        } catch (error) {
          console.error('Error prefilling form data:', error);
        }
      };
      
      getProjectData();
    }
  }, [currentPermit, projectId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (currentPermit && currentPermit._id) {
        await dispatch(updatePermitForm({
          projectId,
          permitId: currentPermit._id,
          formData: {
            ...currentPermit,
            formData
          }
        })).unwrap();
        showToast('Bid Notes updated successfully', 'success');
      } else {
        await dispatch(savePermitForm({
          projectId,
          formType: 'bidNotes',
          formData
        })).unwrap();
        showToast('Bid Notes saved successfully', 'success');
      }
      onClose();
    } catch (error) {
      showToast(error.message || 'Failed to save Bid Notes', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDownload = () => {
    // Create a printable version of the form content
    const formContent = `
      ${createCompanyHeader()}
      <h1 class="form-title">Bid Notes</h1>
      
      <div class="form-section">
        ${formatFormField('Job Number', formData.jobNumber)}
        ${formatFormField('Date', formData.date)}
      </div>
      
      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th>Quantity</th>
          </tr>
        </thead>
        <tbody>
          ${formatTableRow('SQUARE FOOT AGE(SF)', formData.squareFootage)}
          ${formatTableRow('ROOF PITCH', formData.roofPitch)}
          ${formatTableRow('EAVE(LF)', formData.eaveLength)}
          ${formatTableRow('RAKE', formData.rakeLength)}
          ${formatTableRow('RIDGE(LF)', formData.ridgeLength)}
          ${formatTableRow('VALLEY(LF)', formData.valleyLength)}
          ${formatTableRow('EVE/RAKE(LF)', formData.eveRakeLength)}
          ${formatTableRow('HWF (LF)', formData.hwfLength)}
          ${formatTableRow('SWF (LF)', formData.swfLength)}
          ${formatTableRow('DTW(LF)', formData.dtwLength)}
          ${formatTableRow('DRIP EDGE(LF)', formData.dripEdgeLength)}
          ${formatTableRow('COPING(LF)', formData.copingLength)}
          ${formatTableRow('WALLS(LF)', formData.wallsLength)}
          ${formatTableRow('DRAINS', formData.drains)}
          ${formatTableRow('SCUPPERS', formData.scuppers)}
          ${formatTableRow('OVERFLOWS', formData.overflows)}
          ${formatTableRow('GUTTERS (LF)', formData.guttersLength)}
          ${formatTableRow('DOWN SPOUTS (LF)', formData.downSpoutsLength)}
          ${formatTableRow('LEADER HEADS (Q)', formData.leaderHeadsQty)}
        </tbody>
      </table>
      
      <div class="form-section">
        <div class="form-label">NOTES:</div>
        <div class="form-value" style="white-space: pre-wrap;">${formData.notes || ''}</div>
      </div>
    `;
    
    // Print the form
    printPermitForm(formData, 'Bid Notes', formContent);
  };

  return (
    <div className="permit-form-shell bg-white p-6 rounded-lg max-w-5xl mx-auto relative">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <img 
            src="/logo/JRT-logo-small.jpg" 
            alt="JRT Roofing & Waterproofing" 
            className="h-16 mr-4"
          />
          <div>
            <h2 className="text-2xl font-bold">JRT ROOFING & WATERPROOFING</h2>
            <p className="text-sm">QLTY CRAFTSMANSHIP</p>
          </div>
        </div>
        <div className="text-right">
          <p>P.O. Box 3688</p>
          <p>San Clemente, CA 92672</p>
          <p>Phone: (949) 742-2892</p>
          <p className="uppercase">JRTROOFING.COM</p>
          <p>CA 1070037</p>
        </div>
      </div>

      <h1 className="text-2xl font-bold mb-6">Bid Notes</h1>
      
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="flex items-center">
          <label className="block text-sm font-medium text-gray-700 w-24">Job Number:</label>
          <input
            type="text"
            name="jobNumber"
            value={formData.jobNumber}
            onChange={handleChange}
            className="flex-1 p-2 border border-gray-300 rounded"
          />
        </div>
        <div className="flex items-center">
          <label className="block text-sm font-medium text-gray-700 w-24">Date:</label>
          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            className="flex-1 p-2 border border-gray-300 rounded"
          />
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300 mb-6">
            <thead>
              <tr>
                <th className="border border-gray-300 p-2 w-1/3 text-left">Item</th>
                <th className="border border-gray-300 p-2 w-2/3 text-left">Quantity</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-300 p-2">SQUARE FOOT AGE(SF)</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="squareFootage"
                    value={formData.squareFootage}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">ROOF PITCH</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="roofPitch"
                    value={formData.roofPitch}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">EAVE(LF)</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="eaveLength"
                    value={formData.eaveLength}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">RAKE-</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="rakeLength"
                    value={formData.rakeLength}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">RIDGE-(LF)</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="ridgeLength"
                    value={formData.ridgeLength}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">VALLEY(LF)-</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="valleyLength"
                    value={formData.valleyLength}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">EVE/RAKE(LF)</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="eveRakeLength"
                    value={formData.eveRakeLength}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">HWF (LF)-</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="hwfLength"
                    value={formData.hwfLength}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">SWF (LF)-</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="swfLength"
                    value={formData.swfLength}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">DTW(LF)-</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="dtwLength"
                    value={formData.dtwLength}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">DRIP EDGE(LF)-</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="dripEdgeLength"
                    value={formData.dripEdgeLength}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">COPING(LF)-</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="copingLength"
                    value={formData.copingLength}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">WALLS(LF)-</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="wallsLength"
                    value={formData.wallsLength}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">DRAINS-</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="drains"
                    value={formData.drains}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">SCUPPERS</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="scuppers"
                    value={formData.scuppers}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">OVERFLOWS</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="overflows"
                    value={formData.overflows}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">GUTTERS (LF)</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="guttersLength"
                    value={formData.guttersLength}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">DOWN SPOUTS (LF)</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="downSpoutsLength"
                    value={formData.downSpoutsLength}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2">LEADER HEADS (Q)</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="leaderHeadsQty"
                    value={formData.leaderHeadsQty}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">NOTES:</label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={6}
            className="w-full p-2 border border-gray-300 bg-blue-50"
          ></textarea>
        </div>

        {/* Add padding at the bottom to ensure content isn't hidden behind the floating bar */}
        <div className="pb-20"></div>
      </form>
      
      {/* Floating Action Bar */}
      <FloatingActionBar 
        onCancel={onClose}
        onDownload={handleDownload}
        onSubmit={handleSubmit}
        loading={loading}
        isUpdate={!!(currentPermit && currentPermit._id)}
      />
    </div>
  );
};

export default BidNotesForm;